import React from "react";

import {
  Autocomplete,
  Checkbox,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { ReportGeneratorIcon } from "assets/material-custom-icons";
import { InvestorAutocomplete } from "components/Autocompletes/InvestorAutocomplete";
import dayjs from "dayjs";
import { useDateFormatSelect, useDocumentTypesSelect, useTaskDealsSelect } from "store";
import { DealBase } from "store/types";

import { FileValues } from "./FilesUpload";

const BORDER_DIVIDER = "1px solid #dbe8f5";
const CELL_PADDING = { py: 1, px: 1.25 };

const StyledTableContainer = styled(TableContainer)({
  borderRadius: "8px",
  border: BORDER_DIVIDER,
  overflow: "hidden"
});

const StyledHeaderRow = styled(TableRow)({
  backgroundColor: "#f6f8fc"
});

const StyledTableCellBase = styled(TableCell)({
  borderBottom: BORDER_DIVIDER,
  ...CELL_PADDING
});

const StyledTableCellHeader = styled(StyledTableCellBase)({
  fontSize: 14,
  fontWeight: 500,
  color: "#071f35"
});

const StyledTableCellHeaderFirst = styled(StyledTableCellHeader)({
  pl: 2,
  pr: 1.25
});

const StyledTableCellCheckbox = styled(TableCell)<{ $header?: boolean }>(({ $header }) => ({
  width: 32,
  minWidth: 32,
  borderBottom: BORDER_DIVIDER,
  py: $header ? 0 : 0.625
}));

const StyledTableCellBody = styled(StyledTableCellBase)({
  fontSize: 14,
  color: "#071f35"
});

const StyledTableRow = styled(TableRow)({
  "& td": { borderBottom: BORDER_DIVIDER },
  "&:last-child td": { borderBottom: 0 }
});

const StyledFileName = styled(Typography)({
  fontSize: 14,
  color: "#071f35",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: 200
});

const autocompleteSx = {
  "& .MuiOutlinedInput-root": {
    height: 32,
    minHeight: 32,
    "& .MuiInputBase-input": { py: 0 },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#7b92a9" }
  },
  fontSize: 14
};

export interface Props {
  filesValues: FileValues[];
  setFilesValues: React.Dispatch<React.SetStateAction<FileValues[]>>;
  updateDocumentType: (index: number, value: string | null) => void;
  updateEffectiveDate?: (index: number, value: string | null) => void;
  dealInvestors: { id: number; name: string }[];
  dealInvestorsLoading: boolean;
  selected: Set<number>;
  setSelected: React.Dispatch<React.SetStateAction<Set<number>>>;
  isCloudMirror?: boolean;
  dealInvestorsMap?: Record<number, { id: number; name: string }[]>;
  onDealChange?: (index: number, deal: DealBase | null) => void;
  lockedDealId?: number | null;
}

export const FilesTable: React.FC<Props> = ({
  filesValues,
  setFilesValues,
  updateDocumentType,
  updateEffectiveDate,
  selected,
  setSelected,
  dealInvestors,
  dealInvestorsLoading,
  isCloudMirror,
  dealInvestorsMap,
  onDealChange,
  lockedDealId
}) => {
  const documentTypes = useDocumentTypesSelect();
  const deals = useTaskDealsSelect();
  const dateFormat = useDateFormatSelect();

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelected(event.target.checked ? new Set(filesValues.map((_, i) => i)) : new Set());
  };

  const handleSelectOne = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = new Set(selected);
    if (event.target.checked) next.add(index);
    else next.delete(index);
    setSelected(next);
  };

  // if (!files.length) return null;

  return (
    <StyledTableContainer sx={{ maxHeight: 250, overflow: "auto" }}>
      <Table stickyHeader size="small">
        <TableHead>
          <StyledHeaderRow>
            <StyledTableCellCheckbox padding="checkbox" $header>
              <Checkbox
                indeterminate={selected.size > 0 && selected.size < filesValues.length}
                checked={selected.size === filesValues.length && filesValues.length > 0}
                onChange={handleSelectAll}
                sx={{ p: 0.5 }}
              />
            </StyledTableCellCheckbox>
            <StyledTableCellHeaderFirst>File name</StyledTableCellHeaderFirst>
            {isCloudMirror && <StyledTableCellHeader>Deal</StyledTableCellHeader>}
            <StyledTableCellHeader>Classification</StyledTableCellHeader>
            <StyledTableCellHeader>Investor</StyledTableCellHeader>
            {isCloudMirror && <StyledTableCellHeader>Effective date</StyledTableCellHeader>}
          </StyledHeaderRow>
        </TableHead>
        <TableBody>
          {filesValues.map((fileValue, index) => {
            const { file, document_type, investor_id, document_type_ai_suggested } = fileValue;
            const rowInvestors =
              isCloudMirror && fileValue.deal_id && dealInvestorsMap
                ? dealInvestorsMap[fileValue.deal_id] || []
                : dealInvestors;
            const rowInvestorsLoading =
              isCloudMirror && fileValue.deal_id && dealInvestorsMap
                ? !dealInvestorsMap[fileValue.deal_id]
                : dealInvestorsLoading;

            return (
              <StyledTableRow key={`${file.name}-${index}`}>
                <StyledTableCellCheckbox padding="checkbox">
                  <Checkbox checked={selected.has(index)} onChange={handleSelectOne(index)} sx={{ p: 0.5 }} />
                </StyledTableCellCheckbox>
                <StyledTableCellBody>
                  <StyledFileName>{file.name}</StyledFileName>
                </StyledTableCellBody>
                {isCloudMirror && (
                  <StyledTableCellBase sx={{ minWidth: 150 }}>
                    <Autocomplete<DealBase>
                      fullWidth
                      size="small"
                      disabled={!!lockedDealId}
                      options={deals}
                      value={deals.find((d) => d.id === fileValue.deal_id) || null}
                      onChange={(_, value) => onDealChange?.(index, value)}
                      getOptionLabel={(opt) => opt.title}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          {option.title}
                        </li>
                      )}
                      renderInput={(params) => <TextField {...params} placeholder="Select deal" />}
                      sx={autocompleteSx}
                    />
                  </StyledTableCellBase>
                )}
                <StyledTableCellBase sx={{ minWidth: 150 }}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    {document_type_ai_suggested && (
                      <ReportGeneratorIcon sx={{ fontSize: 20, color: "#c09560", flexShrink: 0 }} />
                    )}
                    <Autocomplete<string>
                      fullWidth
                      size="small"
                      options={documentTypes}
                      value={document_type}
                      onChange={(_, value) => updateDocumentType(index, value || null)}
                      getOptionLabel={(opt) => opt}
                      renderInput={(params) => <TextField {...params} placeholder="Select" />}
                      sx={autocompleteSx}
                    />
                  </Stack>
                </StyledTableCellBase>
                <StyledTableCellBase sx={{ minWidth: 150 }}>
                  <InvestorAutocomplete
                    sx={autocompleteSx}
                    label=""
                    loading={rowInvestorsLoading}
                    dealInvestors={rowInvestors}
                    preselectedInvestorId={investor_id || null}
                    disabled={isCloudMirror && !fileValue.deal_id}
                    onChange={(value) => {
                      setFilesValues((prevState) =>
                        prevState.map((item, i) => (i === index ? { ...item, investor_id: value?.id || null } : item))
                      );
                    }}
                  />
                </StyledTableCellBase>
                {isCloudMirror && (
                  <StyledTableCellBase sx={{ minWidth: 150 }}>
                    <DesktopDatePicker
                      value={fileValue.effective_date ? dayjs(fileValue.effective_date) : null}
                      onChange={(newValue) =>
                        updateEffectiveDate?.(index, newValue?.isValid() ? newValue.format("YYYY-MM-DD") : null)
                      }
                      format={dateFormat}
                      slotProps={{
                        field: { clearable: true, onClear: () => updateEffectiveDate?.(index, null) },
                        textField: { placeholder: "Select date" }
                      }}
                      sx={autocompleteSx}
                    />
                  </StyledTableCellBase>
                )}
              </StyledTableRow>
            );
          })}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};
